/**
 * Shared MIME → file-extension mapping used by anything that needs to
 * turn a `Blob`'s MIME type into a sensible filename suffix.
 *
 * Phase 6 (GitHub persistence) lifted this from
 * `src/features/asset-locker/AssetLocker.tsx` so the AssetLocker store
 * (which mints `<id>.<ext>` filenames on the upload path) and the
 * lightbox download anchor (which appends the same extension when
 * deriving a human-friendly download name) can share one source of
 * truth. Adding a new MIME here flows through to both places without a
 * second edit.
 *
 * The companion `mimeFromExt()` helper is the reverse direction —
 * used by `useAssetLocker.sync()` when pulling unknown files down from
 * GitHub (we only have the path's extension to work from at that point;
 * GitHub doesn't carry MIME types in the Contents API).
 */

/** MIME type → extension (without leading dot). */
export const MIME_EXT: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "text/markdown": "md",
  "text/plain": "txt",
  "text/csv": "csv",
  "application/json": "json",
  "application/pdf": "pdf",
  // Zip archives — the seeded `.zip` deliverables are real jszip
  // bundles now; without this row a pulled seed resolved to
  // `application/octet-stream` and downloaded extensionless.
  "application/zip": "zip",
  // Office documents — without these a dropped Word/Excel/PowerPoint
  // deliverable minted an extensionless blob name, and the ingestion
  // worker (which routes on the blob path's extension) skipped it, so
  // the XAES Analyst could never see it.
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  // Audio/video — used by the transcript media-playback slice (and harmlessly
  // sharpens AssetLocker's tag for any media upload).
  "audio/mpeg": "mp3",
  "audio/mp4": "m4a",
  "audio/wav": "wav",
  "audio/webm": "weba",
  "audio/ogg": "ogg",
  "audio/aac": "aac",
  "audio/flac": "flac",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/ogg": "ogv",
  "video/quicktime": "mov",
};

/**
 * Reverse of {@link MIME_EXT}. Returns a best-guess MIME for a file
 * extension; falls back to `application/octet-stream` for anything not
 * in the table so the caller still gets a valid `Blob.type`.
 *
 * Lowercases the input — GitHub paths are case-sensitive but
 * conventional extensions never are.
 */
export function mimeFromExt(ext: string): string {
  const clean = ext.replace(/^\.+/, "").toLowerCase();
  for (const [mime, e] of Object.entries(MIME_EXT)) {
    if (e === clean) return mime;
  }
  // A couple of common aliases the forward table doesn't carry.
  if (clean === "jpeg") return "image/jpeg";
  if (clean === "markdown") return "text/markdown";
  if (clean === "m4v") return "video/mp4";
  if (clean === "oga") return "audio/ogg";
  return "application/octet-stream";
}

/**
 * Classify a MIME type as playable audio or video, or `null` when it's
 * neither. Used by the transcript playback modal to pick `<audio>` vs
 * `<video>` and to gate the "attach media" affordance.
 */
export function mediaKindFromMime(mime: string): "audio" | "video" | null {
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return null;
}

/**
 * Pull the extension off a filename or path. Returns `""` when there's
 * no `.` in the basename (matches the convention that a dotless name
 * has no extension; `.gitignore` etc. would also resolve to `""`).
 */
export function extFromFilename(filename: string): string {
  // Strip directory portion first so a dot in a path segment doesn't
  // mask an extensionless filename.
  const base = filename.split(/[\\/]/).pop() ?? filename;
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "";
  return base.slice(dot + 1).toLowerCase();
}
